import { packContainer } from '@/features/import-tracking/utils/bin-packing';
import {
  checkPlacement,
  previewPlacement,
  type ContainerBounds,
  type LayoutBox,
} from '@/features/import-tracking/utils/container-layout';
import type {
  ContenedorDeComercia,
  ProductoDeComercia,
  TipoDeContenedor,
  UnidadUbicada,
} from '../types/comercia.types';

/**
 * La lógica de Comercia › Carga, sin three.js: qué unidades hay, dónde están,
 * qué contenedor conviene, en qué orden se cargan y cómo se reparte el peso.
 *
 * Convención de ejes (la del acomodo de toda la app): x es el largo y arranca en
 * el FONDO (x = 0, la pared del frente del contenedor) y termina en la PUERTA
 * (x = largo); y es el ancho; z es el alto. Se carga del fondo a la puerta.
 */

/** El id de una unidad dentro del editor: `<productoId>#<n>`. */
export const idDeUnidad = (productoId: string, n: number) => `${productoId}#${n}`;
export const productoDeUnidad = (idUnidad: string) => idUnidad.split('#')[0];

/** Las medidas de un producto como caja del acomodo, rotada o no. */
function caja(p: ProductoDeComercia, id: string, u?: UnidadUbicada): LayoutBox {
  const r = u?.r ?? false;
  return {
    id,
    x: u?.x ?? 0,
    y: u?.y ?? 0,
    z: u?.z ?? 0,
    lengthCm: r ? p.anchoCm! : p.largoCm!,
    widthCm: r ? p.largoCm! : p.anchoCm!,
    heightCm: p.altoCm!,
    rotated: r,
    weightKg: p.pesoUnitario ?? 0,
  };
}

const tieneMedidas = (p: ProductoDeComercia) =>
  (p.largoCm ?? 0) > 0 && (p.anchoCm ?? 0) > 0 && (p.altoCm ?? 0) > 0;

/**
 * Lo que el editor de UN contenedor necesita: las unidades ya ubicadas en él y
 * la bandeja con las que no están en ningún contenedor. Las que están en otro
 * contenedor no aparecen (son de ese otro).
 */
export function unidadesDelContenedor(
  productos: ProductoDeComercia[],
  contenedores: ContenedorDeComercia[],
  contenedorId: string
): { placed: LayoutBox[]; tray: LayoutBox[] } {
  const placed: LayoutBox[] = [];
  const tray: LayoutBox[] = [];
  const este = contenedores.find((c) => c.id === contenedorId);
  for (const p of productos) {
    if (!tieneMedidas(p)) continue;
    const enOtros = contenedores
      .filter((c) => c.id !== contenedorId)
      .flatMap((c) => c.lineas)
      .filter((l) => l.productoId === p.id)
      .reduce((s, l) => s + l.cantidad, 0);
    const aca = este?.lineas.filter((l) => l.productoId === p.id).flatMap((l) => l.unidades) ?? [];
    aca.forEach((u, n) => placed.push(caja(p, idDeUnidad(p.id, n), u)));
    const libres = Math.max(0, p.cantidad - enOtros - aca.length);
    for (let n = 0; n < libres; n++) tray.push(caja(p, idDeUnidad(p.id, aca.length + n)));
  }
  return { placed, tray };
}

/** Las unidades acomodadas, agrupadas por producto, como las guarda el servidor. */
export function lineasParaGuardar(placed: LayoutBox[]) {
  const porProducto = new Map<string, UnidadUbicada[]>();
  for (const b of placed) {
    const id = productoDeUnidad(b.id);
    const lista = porProducto.get(id) ?? [];
    lista.push({ x: redondo(b.x), y: redondo(b.y), z: redondo(b.z), r: b.rotated });
    porProducto.set(id, lista);
  }
  return [...porProducto.entries()].map(([productoId, unidades]) => ({ productoId, unidades }));
}

const redondo = (n: number) => Math.round(n * 10) / 10;

export const limitesDe = (t: TipoDeContenedor): ContainerBounds => ({
  lengthCm: t.largoCm,
  widthCm: t.anchoCm,
  heightCm: t.altoCm,
  maxWeightKg: t.pesoMaxKg,
});

// ── Resumen de la carga ─────────────────────────────────────────────────────

/** El pallet de referencia: 120 × 100 cm, hasta 1,5 m de carga. */
export const PALLET = { largoCm: 120, anchoCm: 100, altoCm: 150 };

export function resumenDeLaCarga(productos: ProductoDeComercia[]) {
  const conMedidas = productos.filter(tieneMedidas);
  const bultos = productos.reduce((s, p) => s + p.cantidad, 0);
  const pesoKg = productos.reduce((s, p) => s + (p.pesoUnitario ?? 0) * p.cantidad, 0);
  const volumenM3 = productos.reduce((s, p) => s + (p.volumenUnitario ?? 0) * p.cantidad, 0);
  const mayor = [...conMedidas].sort(
    (a, b) => (b.volumenUnitario ?? 0) - (a.volumenUnitario ?? 0)
  )[0];
  const volumenPallet = (PALLET.largoCm * PALLET.anchoCm * PALLET.altoCm) / 1_000_000;
  return {
    bultos,
    pesoKg,
    volumenM3,
    /** Cuántos pallets harían falta si se paletiza (por volumen, contra el de referencia). */
    pallets: volumenM3 > 0 ? Math.ceil(volumenM3 / volumenPallet) : 0,
    mayor: mayor ? { largo: mayor.largoCm!, ancho: mayor.anchoCm!, alto: mayor.altoCm! } : null,
    fragil: productos.some((p) => p.fragil),
    sinMedidas: productos.filter((p) => !tieneMedidas(p)).length,
  };
}

// ── Qué contenedor conviene ─────────────────────────────────────────────────

export interface Recomendacion {
  tipo: TipoDeContenedor;
  cantidad: number;
  /** Del total de la carga, qué parte del volumen y del peso de los contenedores usa. */
  usoVolumen: number;
  usoPeso: number;
}

/** Más de esto, la cuenta se hace por volumen (empaquetar miles de bultos congela la pantalla). */
const TOPE_PARA_EMPAQUETAR = 1500;
/** Cuánto del volumen de un contenedor se llena en la práctica con bultos sueltos. */
const APROVECHAMIENTO = 0.85;

/**
 * El contenedor que conviene: el más chico donde entra todo, probándolo de
 * verdad con el mismo empaquetado del botón «Auto-acomodar» (las medidas de los
 * bultos mandan, no sólo los m³). Si no entra en uno, cuántos del más grande.
 */
export function recomendarContenedor(
  productos: ProductoDeComercia[],
  tipos: TipoDeContenedor[]
): Recomendacion | null {
  const items = productos.filter(tieneMedidas).map((p) => ({
    id: p.id,
    lengthCm: p.largoCm!,
    widthCm: p.anchoCm!,
    heightCm: p.altoCm!,
    weightKg: (p.pesoUnitario ?? 0) * p.cantidad,
    quantity: p.cantidad,
    color: p.color,
    fragile: p.fragil,
  }));
  if (items.length === 0 || tipos.length === 0) return null;
  const bultos = items.reduce((s, i) => s + i.quantity, 0);
  const volumen = items.reduce(
    (s, i) => s + (i.lengthCm * i.widthCm * i.heightCm * i.quantity) / 1e6,
    0
  );
  const peso = items.reduce((s, i) => s + i.weightKg, 0);
  const ordenados = [...tipos].sort((a, b) => a.volumenM3 - b.volumenM3);

  const entraEnUno = (t: TipoDeContenedor) => {
    if (volumen > t.volumenM3 || peso > t.pesoMaxKg) return false;
    if (bultos > TOPE_PARA_EMPAQUETAR) return volumen <= t.volumenM3 * APROVECHAMIENTO;
    const r = packContainer({ ...limitesDe(t), maxVolumeCbm: t.volumenM3 }, items);
    return r.placements.length === bultos;
  };

  const uso = (t: TipoDeContenedor, n: number) => ({
    usoVolumen: (volumen / (t.volumenM3 * n)) * 100,
    usoPeso: t.pesoMaxKg > 0 ? (peso / (t.pesoMaxKg * n)) * 100 : 0,
  });

  for (const t of ordenados) {
    if (entraEnUno(t)) return { tipo: t, cantidad: 1, ...uso(t, 1) };
  }
  // No entra en uno: cuántos del más grande, por volumen y por peso.
  const grande = ordenados[ordenados.length - 1];
  const n = Math.max(
    Math.ceil(volumen / (grande.volumenM3 * APROVECHAMIENTO)),
    grande.pesoMaxKg > 0 ? Math.ceil(peso / grande.pesoMaxKg) : 1,
    2
  );
  return { tipo: grande, cantidad: n, ...uso(grande, n) };
}

// ── Acomodo ─────────────────────────────────────────────────────────────────

/**
 * Auto-acomodar: todas las unidades del contenedor más las de la bandeja, con el
 * empaquetado de la app (las grandes primero, del fondo a la puerta, nada
 * flotando y sobre lo frágil sólo el mismo producto). Lo que no entra vuelve a
 * la bandeja.
 *
 * La carga queda en un bloque contra el FONDO, como se estiba de verdad: la
 * pared del frente frena la carga en las frenadas y el espacio libre queda de
 * un solo lado, junto a la puerta, donde se traba (ver `libreEnElLargo`).
 *
 * 🔴 Antes se corría al medio para no salir con el aviso de «peso al fondo».
 * Con una carga liviana eso dejaba aire adelante y atrás: la carga podía
 * correrse para los dos lados y golpearse contra las paredes en el viaje.
 */
export function autoAcomodar(
  todas: LayoutBox[],
  limites: ContainerBounds,
  fragiles: ReadonlySet<string>
): { placed: LayoutBox[]; tray: LayoutBox[] } {
  const porProducto = new Map<string, LayoutBox[]>();
  for (const b of todas) {
    const id = productoDeUnidad(b.id);
    porProducto.set(id, [...(porProducto.get(id) ?? []), b]);
  }
  const base = new Map(
    [...porProducto.entries()].map(([id, us]) => {
      const u = us[0];
      return [
        id,
        {
          lengthCm: u.rotated ? u.widthCm : u.lengthCm,
          widthCm: u.rotated ? u.lengthCm : u.widthCm,
          heightCm: u.heightCm,
          weightKg: u.weightKg,
        },
      ] as const;
    })
  );
  const r = packContainer(
    {
      ...limites,
      maxVolumeCbm: (limites.lengthCm * limites.widthCm * limites.heightCm) / 1e6,
    },
    [...porProducto.entries()].map(([id, us]) => ({
      id,
      ...base.get(id)!,
      weightKg: base.get(id)!.weightKg * us.length,
      quantity: us.length,
      color: '#000000',
      fragile: fragiles.has(id),
    }))
  );
  const usadas = new Map<string, number>();
  const placed: LayoutBox[] = [];
  for (const p of r.placements) {
    const id = p.itemId.split('_')[0];
    const n = usadas.get(id) ?? 0;
    usadas.set(id, n + 1);
    const modelo = porProducto.get(id)?.[n];
    const m = base.get(id);
    if (!modelo || !m) continue;
    placed.push({
      ...modelo,
      x: p.x,
      y: p.y,
      z: p.z,
      rotated: p.rotated,
      lengthCm: p.rotated ? m.widthCm : m.lengthCm,
      widthCm: p.rotated ? m.lengthCm : m.widthCm,
      heightCm: m.heightCm,
    });
  }
  const ids = new Set(placed.map((b) => b.id));
  return {
    placed,
    tray: todas.filter((b) => !ids.has(b.id)).map((b) => ({ ...b, x: 0, y: 0, z: 0 })),
  };
}

/**
 * ¿Esta caja quedaría apoyada sobre algo frágil de otro producto? Sobre el
 * mismo producto sí se apila: son cajas iguales (ver `tieneApoyo` en el
 * empaquetado).
 */
export function apoyaSobreFragil(
  b: LayoutBox,
  otras: LayoutBox[],
  fragiles: ReadonlySet<string>
): boolean {
  if (b.z <= 0.01) return false;
  return otras.some(
    (o) =>
      o.id !== b.id &&
      fragiles.has(productoDeUnidad(o.id)) &&
      productoDeUnidad(o.id) !== productoDeUnidad(b.id) &&
      Math.abs(o.z + o.heightCm - b.z) < 0.5 &&
      Math.min(b.x + b.lengthCm, o.x + o.lengthCm) - Math.max(b.x, o.x) > 0 &&
      Math.min(b.y + b.widthCm, o.y + o.widthCm) - Math.max(b.y, o.y) > 0
  );
}

/**
 * Ubica unidades de la bandeja SIN mover las que ya están (el botón «Ubicar»):
 * prueba las esquinas libres que dejan las cajas puestas, del fondo a la puerta,
 * con las mismas reglas del arrastre (paredes, choques, peso y gravedad).
 */
export function ubicarSinMover(
  placed: LayoutBox[],
  nuevas: LayoutBox[],
  limites: ContainerBounds,
  fragiles: ReadonlySet<string>
): { placed: LayoutBox[]; sinLugar: LayoutBox[] } {
  const resultado = [...placed];
  const sinLugar: LayoutBox[] = [];

  /*
    Las esquinas libres, sin repetir y del fondo a la puerta: se arman una vez y
    se suman las de cada caja que entra.

    🔴 Se rearmaban y se probaban todas para cada caja: «Ubicar 300» con 190 ya
    puestas congelaba la pantalla 6 segundos (ensayo de Comercia del 30-oct).
  */
  const candidatos: Array<{ x: number; y: number }> = [];
  const vistos = new Set<string>();
  const sumar = (x: number, y: number) => {
    const clave = `${x},${y}`;
    if (vistos.has(clave)) return;
    vistos.add(clave);
    let i = candidatos.length;
    while (
      i > 0 &&
      (candidatos[i - 1].x > x || (candidatos[i - 1].x === x && candidatos[i - 1].y > y))
    )
      i--;
    candidatos.splice(i, 0, { x, y });
  };
  const esquinasDe = (p: LayoutBox) => {
    sumar(p.x + p.lengthCm, p.y);
    sumar(p.x, p.y + p.widthCm);
    sumar(p.x, p.y);
  };
  sumar(0, 0);
  for (const p of resultado) esquinasDe(p);

  /*
    Dónde no entra una caja de tales medidas y peso: se sale del contenedor o
    pasa el peso. Sumar cajas sólo sube la altura de apoyo y el peso total, así
    que ese lugar no vuelve a servir y no se prueba de nuevo. Apoyar sobre algo
    frágil sí puede cambiar, y eso se vuelve a mirar cada vez.
  */
  const noEntra = new Set<string>();

  for (const caja0 of nuevas) {
    const rotada = {
      ...caja0,
      lengthCm: caja0.widthCm,
      widthCm: caja0.lengthCm,
      rotated: !caja0.rotated,
    };
    let puesta: LayoutBox | null = null;
    buscar: for (const c of candidatos) {
      for (const orientacion of [caja0, rotada]) {
        const clave = `${orientacion.lengthCm}x${orientacion.widthCm}x${orientacion.heightCm}x${orientacion.weightKg}@${c.x},${c.y}`;
        if (noEntra.has(clave)) continue;
        const r = previewPlacement(orientacion, c.x, c.y, resultado, limites, 1);
        if (!r.check.valid) {
          noEntra.add(clave);
          continue;
        }
        if (!apoyaSobreFragil(r.box, resultado, fragiles)) {
          puesta = r.box;
          break buscar;
        }
      }
    }
    if (puesta) {
      resultado.push(puesta);
      esquinasDe(puesta);
    } else sinLugar.push(caja0);
  }
  return { placed: resultado, sinLugar };
}

// ── Peso y secuencia ────────────────────────────────────────────────────────

/**
 * Qué parte del peso va en la mitad del fondo (x < largo/2). Una caja que
 * cruza el medio aporta a cada mitad según el largo que tiene de cada lado.
 */
export function pesoAlFondo(placed: LayoutBox[], largoCm: number): number {
  const total = placed.reduce((s, b) => s + b.weightKg, 0);
  if (total <= 0) return 0;
  const medio = largoCm / 2;
  const fondo = placed.reduce((s, b) => {
    const parte = Math.min(1, Math.max(0, (medio - b.x) / b.lengthCm));
    return s + b.weightKg * parte;
  }, 0);
  return (fondo / total) * 100;
}

/** Más de esto de un lado, conviene equilibrar (el mismo criterio del editor de la app). */
export const DESBALANCE = 60;

/**
 * Desde qué parte de lo que aguanta el contenedor importa cómo se reparte el
 * peso a lo largo. La regla del 60/40 es para que la grúa lo levante derecho y
 * el camión no cargue un solo eje: con 1,3 t en un contenedor de 28 t, aunque
 * todo vaya al fondo, ni la grúa ni el camión lo notan. Con poca carga lo que
 * importa es trabarla para que no se corra.
 */
export const PESO_QUE_IMPORTA = 0.3;

/** ¿Hay que mirar el reparto del peso? Sólo con carga pesada (ver `PESO_QUE_IMPORTA`). */
export function importaElReparto(pesoKg: number, pesoMaxKg: number): boolean {
  return pesoMaxKg > 0 && pesoKg >= pesoMaxKg * PESO_QUE_IMPORTA;
}

/**
 * Cuánto largo del contenedor queda sin carga (en cm), sumando lo que haya
 * contra el fondo y contra la puerta. Ese aire hay que llenarlo o trabarlo
 * (tablas, bolsas de aire) para que la carga no se corra en el viaje.
 */
export function libreEnElLargo(placed: LayoutBox[], largoCm: number): number {
  if (placed.length === 0) return largoCm;
  const desde = Math.min(...placed.map((b) => b.x));
  const hasta = Math.max(...placed.map((b) => b.x + b.lengthCm));
  return Math.max(0, largoCm - (hasta - desde));
}

/**
 * Equilibrar: corre toda la carga a lo largo para que el centro de su peso quede
 * en el medio del contenedor, sin cambiar cómo está armada. Sirve cuando sobra
 * largo; si la carga llena el contenedor, no hay a dónde correrla.
 */
export function equilibrar(
  placed: LayoutBox[],
  limites: ContainerBounds
): { placed: LayoutBox[]; movido: number } {
  const total = placed.reduce((s, b) => s + b.weightKg, 0);
  if (placed.length === 0 || total <= 0) return { placed, movido: 0 };
  const centro = placed.reduce((s, b) => s + (b.x + b.lengthCm / 2) * b.weightKg, 0) / total;
  const minX = Math.min(...placed.map((b) => b.x));
  const maxX = Math.max(...placed.map((b) => b.x + b.lengthCm));
  const deseado = limites.lengthCm / 2 - centro;
  const dx = Math.max(-minX, Math.min(limites.lengthCm - maxX, deseado));
  const paso = Math.round(dx);
  if (Math.abs(paso) < 1) return { placed, movido: 0 };
  const movidas = placed.map((b) => ({ ...b, x: b.x + paso }));
  // Por las dudas: si algo quedara fuera, no se toca.
  const ok = movidas.every((b) => checkPlacement(b, [], limites).valid);
  return ok ? { placed: movidas, movido: paso } : { placed, movido: 0 };
}

/**
 * La secuencia de carga en tandas: qué entra primero (fondo, abajo) y qué al
 * final (puerta). Se agrupan las unidades seguidas del mismo producto.
 */
export function secuenciaDeCarga(placed: LayoutBox[]) {
  const orden = [...placed].sort((a, b) => a.x - b.x || a.z - b.z || a.y - b.y);
  const tandas: Array<{ productoId: string; cantidad: number; desde: number; hasta: number }> = [];
  for (const b of orden) {
    const id = productoDeUnidad(b.id);
    const ultima = tandas[tandas.length - 1];
    if (ultima && ultima.productoId === id) {
      ultima.cantidad += 1;
      ultima.hasta = Math.max(ultima.hasta, b.x + b.lengthCm);
    } else tandas.push({ productoId: id, cantidad: 1, desde: b.x, hasta: b.x + b.lengthCm });
  }
  // Tandas cortas del mismo producto que se intercalan quedan juntas por producto.
  const porProducto = new Map<
    string,
    { productoId: string; cantidad: number; desde: number; hasta: number }
  >();
  for (const t of tandas) {
    const p = porProducto.get(t.productoId);
    if (p) {
      p.cantidad += t.cantidad;
      p.desde = Math.min(p.desde, t.desde);
      p.hasta = Math.max(p.hasta, t.hasta);
    } else porProducto.set(t.productoId, { ...t });
  }
  const ordenadas = [...porProducto.values()].sort((a, b) => a.desde - b.desde);
  return ordenadas.map((t, i) => ({
    ...t,
    /**
     * Qué tanda va contra el fondo (la primera que se carga) y cuál junto a la
     * puerta (la última). Con una sola, sólo «fondo».
     */
    lugar:
      i === 0
        ? ('fondo' as const)
        : i === ordenadas.length - 1
          ? ('puerta' as const)
          : ('medio' as const),
  }));
}
